import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { useI18n } from "@expertloop/i18n/react";

/** Largeur par défaut de la colonne du dossier, et bornes de son redimensionnement. */
const DEFAULT_LEFT_WIDTH = 1200;
const MIN_LEFT_WIDTH = 480;
/** Largeur minimale de la colonne de conversation. */
const MIN_RIGHT_WIDTH = 352;
const HANDLE_WIDTH = 10;
const KEYBOARD_STEP = 40;
const STORAGE_KEY = "expertloop.admin.mediation.leftWidth";

function readStoredWidth(): number | null {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		const value = raw === null ? Number.NaN : Number(raw);
		return Number.isFinite(value) && value >= MIN_LEFT_WIDTH ? value : null;
	} catch {
		return null;
	}
}

function storeWidth(width: number | null): void {
	try {
		if (width === null) window.localStorage.removeItem(STORAGE_KEY);
		else window.localStorage.setItem(STORAGE_KEY, String(Math.round(width)));
	} catch {
		// Le réglage n'est qu'un confort : sans stockage, la largeur par défaut revient
	}
}

interface ResizableColumnsProps {
	left: ReactNode;
	right: ReactNode;
}

/**
 * Deux colonnes dont la limite se déplace à la souris (ou au clavier), selon la largeur de
 * l'écran : le dossier à gauche (1 200 pixels au plus par défaut), la conversation à droite. La
 * largeur choisie est retenue dans le navigateur ; un double-clic sur la poignée rétablit la
 * largeur par défaut. Sous 1 100 pixels, les colonnes s'empilent et la poignée disparaît.
 */
export function ResizableColumns({ left, right }: ResizableColumnsProps) {
	const { t } = useI18n();
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState<number | null>(() => readStoredWidth());
	const [dragging, setDragging] = useState(false);

	const clamp = useCallback((value: number) => {
		const total = containerRef.current?.getBoundingClientRect().width ?? Number.POSITIVE_INFINITY;
		const max = Math.max(MIN_LEFT_WIDTH, total - MIN_RIGHT_WIDTH - HANDLE_WIDTH);
		return Math.min(Math.max(value, MIN_LEFT_WIDTH), max);
	}, []);

	const move = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			const box = containerRef.current?.getBoundingClientRect();
			if (!box) return;
			setWidth(clamp(event.clientX - box.left - HANDLE_WIDTH / 2));
		},
		[clamp],
	);

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const current = width ?? Math.min(DEFAULT_LEFT_WIDTH, containerRef.current?.getBoundingClientRect().width ?? DEFAULT_LEFT_WIDTH);
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			setWidth(clamp(current - KEYBOARD_STEP));
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			setWidth(clamp(current + KEYBOARD_STEP));
		}
	};

	// La largeur est retenue une fois le geste terminé, pas à chaque déplacement
	useEffect(() => {
		if (!dragging) storeWidth(width);
	}, [dragging, width]);

	return (
		<div
			ref={containerRef}
			className={`mediation-workstation-layout${dragging ? " mediation-workstation-layout--dragging" : ""}`}
			style={width === null ? undefined : ({ "--mediation-left": `${width}px` } as React.CSSProperties)}
		>
			<div className="mediation-main">{left}</div>
			<div
				className="mediation-splitter"
				role="separator"
				aria-orientation="vertical"
				aria-label={t("adminChatDetail.splitter")}
				aria-valuenow={width === null ? undefined : Math.round(width)}
				aria-valuemin={MIN_LEFT_WIDTH}
				tabIndex={0}
				onPointerDown={(event) => {
					event.currentTarget.setPointerCapture?.(event.pointerId);
					setDragging(true);
				}}
				onPointerMove={(event) => {
					if (dragging) move(event);
				}}
				onPointerUp={(event) => {
					event.currentTarget.releasePointerCapture?.(event.pointerId);
					setDragging(false);
				}}
				onPointerCancel={() => setDragging(false)}
				onDoubleClick={() => setWidth(null)}
				onKeyDown={onKeyDown}
			/>
			<aside className="mediation-conversation-aside">{right}</aside>
		</div>
	);
}
